/**
 * Editable Subject for an email reply.
 *
 * The field is pre-filled with the subject the server would send anyway
 * (POST /conversations/:id/reply-preview renders it through the same code as
 * the send path). Only a subject the agent actually changed is returned as
 * `customSubject` — an untouched or cleared field sends nothing, so the
 * server's default (reply template / "Re: …" + ticket reference) still applies.
 *
 * Threading is unaffected by an edit: replies thread on In-Reply-To, and the
 * server appends the `[TKT-…]` reference when the typed subject lacks it.
 */
import { useEffect, useState } from 'react'
import { api } from '../../../api/client.js'

export interface ReplySubject {
  subject: string
  setSubject: (value: string) => void
  /** The default the field was pre-filled with ('' until loaded). */
  defaultSubject: string
  /** Send as `emailSubject` when defined. */
  customSubject: string | undefined
}

export function useReplySubject(conversationId: string, enabled: boolean): ReplySubject {
  const [defaultSubject, setDefaultSubject] = useState('')
  const [subject, setSubjectState] = useState('')
  const [edited, setEdited] = useState(false)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    void (async () => {
      try {
        const res = await api.post<{ subject?: string }>(`/conversations/${conversationId}/reply-preview`, {})
        const fetched = typeof res?.subject === 'string' ? res.subject : ''
        if (cancelled || !fetched) return
        setDefaultSubject(fetched)
        // Don't clobber anything the agent typed while the request was in flight.
        setSubjectState((current) => (current ? current : fetched))
      } catch {
        // No default to show — the field stays empty and the server default applies.
      }
    })()
    return () => { cancelled = true }
  }, [conversationId, enabled])

  const setSubject = (value: string): void => {
    setEdited(true)
    setSubjectState(value)
  }

  const trimmed = subject.trim()
  const customSubject = edited && trimmed && trimmed !== defaultSubject.trim() ? trimmed : undefined

  return { subject, setSubject, defaultSubject, customSubject }
}
